<template>
  <fieldset>
    <legend>
      <slot></slot>
    </legend>

    <row :class="additionalClass">
      <column :size="fullWidth ? '100%' : '50%'" v-if="typeOptions.length !== 1">
        <div class="sr-only">
          <label :for="prop_key + '_type'" class="a-select-label">{{displayName}} Type</label>
        </div>
        <a-select
          v-model="value.type"
          :options="typeOptions"
          :disabled="typeOptions.length===1"
          :compress="compress"
          :id="prop_key + '_type'"
          :name="prop_key + '_type'"></a-select>
      </column>
      <div v-if="propertyForSection(DurationType.propertyBasedTimeSpan, 'includeWindowComparisonOptions') && value.type == DurationType.propertyBasedTimeSpan">
        <div class="sr-only">
          <label :for="prop_key + '_window_comparison_type'" class="a-select-label">{{displayName}} Window Comparison Type</label>
        </div>
        <a-select
          v-model="value.window_comparison_type"
          :options="windowComparisonOptions"
          :compress="compress"
          :id="prop_key + '_unit'"
          :name="prop_key + '_unit'"></a-select>
      </div>

      <column :size="fullWidth ? '100%' : '50%'">
        <template v-if="value.type === DurationType.customFormula">
          <formula-wrapper
            field-name="derived_value"
            :dynamic-options="dynamicOptions"
          >
            <template #value="slotProps">
              <expandable-textarea
                :key="slotProps.model[slotProps.fieldName]"
                v-model="slotProps.model[slotProps.fieldName]"
                id="formulaValue"
                type="text"
                placeholder="Enter a formula"
                is-multiline
              ></expandable-textarea>
            </template>
          </formula-wrapper>
        </template>
        <template
          v-if="value.type === DurationType.timespan || value.type === DurationType.fixedDateAfterTimeSpan ||  value.type == DurationType.propertyBasedTimeSpan">
          <multi-input full :compress="compress">
            <column>
              <div class="sr-only">
                <label :for="prop_key + '_amount'" class="a-select-label">{{displayName}} Amount</label>
              </div>
              <number-input
                v-model="value.amount"
                :replacement-value="0"
                :id="prop_key + '_amount'"
                :name="prop_key + '_amount'"
              ></number-input>
            </column>
            <column>
              <div class="sr-only">
                <label :for="prop_key + '_unit'" class="a-select-label">{{displayName}} Unit</label>
              </div>
              <a-select
                v-model="value.unit"
                :options="unitOptions"
                :compress="compress"
                :id="prop_key + '_unit'"
                :name="prop_key + '_unit'"></a-select>
            </column>
          </multi-input>
        </template>

        <template
          v-if="value.type === DurationType.fixedDate || value.type === DurationType.fixedDateAfterTimeSpan">
          <div class="sr-only">
            <label :for="prop_key + '_month'" class="a-select-label">{{displayName}} Month</label>
          </div>
          <multi-input full :compress="compress">
            <column>
              <div class="sr-only">
                <label :for="prop_key + '_day'" class="a-select-label">{{displayName}} Day</label>
              </div>
              <a-select
                v-model="value.day"
                :options="numDays"
                :id="prop_key + '_day'"
                :name="prop_key + '_day'"></a-select>
            </column>
            <column>
              <a-select
                v-model="value.month"
                :options="DurationAndTimeHelper.monthOptions"
                :id="prop_key + '_month'"
                :name="prop_key + '_month'"></a-select>
            </column>
          </multi-input>
        </template>
      </column>
      <template  v-if="value.type == DurationType.propertyBasedTimeSpan">
      <column :size="fullWidth ? '100%' : '50%'" >
          <div class="sr-only">
            <label :for="prop_key + '_comparison'" class="a-select-label">Period Start Relative Date Comparison</label>
          </div>
          <a-select
            :compress="compress"
            v-model="value.comparison"
            :options="statusActivationDateComparisonVOptions"
            :id="prop_key + '_comparison'"
          ></a-select>
        </column>

        <column :size="fullWidth ? '100%' : '50%'" class="flex-wrap min-max">
          <template v-if="value.type === DurationType.propertyBasedTimeSpan">
            <label v-if="propertyForSection(DurationType.propertyBasedTimeSpan, 'sectionLabel')" :for="prop_key + '_property'">{{propertyForSection(DurationType.propertyBasedTimeSpan, 'sectionLabel') || 'Period Start Relative Date Property' }}</label>
            <div v-else class="sr-only">
              <label :for="prop_key + '_property'" class="a-select-label">{{propertyForSection(DurationType.propertyBasedTimeSpan, 'sectionLabel') || 'Period Start Relative Date Property' }}</label>
            </div>
            <a-select
              :compress="compress"
              v-model="propertyValue"
              bind-by-object
              :options="propertyForSection(DurationType.propertyBasedTimeSpan, 'dropdownOptions')"
              :id="prop_key + '_property'"
            />
          </template>

        </column>
      </template>
      <template  v-if="value.type === DurationType.datePicker">
        <date-picker
          v-model="value.datetime"
          :compress="compress"
          :mode="DatetimeMode.Date"
        >
        </date-picker>
      </template>
    </row>


  </fieldset>
</template>
<script lang="ts">
  import Vue from "vue";
  import {DurationType} from "../../enums/durationType";
  import DurationAndTimeHelper from "../../helpers/durationAndTimeHelper";
  import {IDefaultVOption, TVOptionSet} from "../../interfaces/vSelectInterfaces";
  import {Month} from "../../enums/month";
  import {DurationUnit} from "../../enums/durationUnit";
  import {IDynamicFieldsOptions} from "../../interfaces/dynamicFieldsInterfaces/dynamicOptionsInterfaces";
  import ObjectModelPropertyDynamicFieldsHandler from "../../models/dynamicFields/handlers/objectModelPropertyDynamicFieldsHandler";
  import FormulaWrapper from "../formulas/formulaWrapper.vue"
  import {statusActivationDateComparisonVOptions, statusActivationEventVOptions} from "~/helpers/statusActivationHelper";
  import DatePicker from "../../components/datePicker.vue"
  import {DatetimeMode} from '~/models/datetimeMode';
  import HandlerOfHandlers from "~/models/dynamicFields/handlers/handlerOfHandlers";
  import ObjectModelRecordMetadataDynamicFieldsHandler from "~/models/dynamicFields/handlers/objectModelRecordMetadataDynamicFieldsHandler";
  import {DynamicReferenceType} from "~/enums/dynamicReferenceType";
  import {EnumHelper} from "~/helpers/enumHelper";
  import {windowComparisonType, windowComparisonTypeName} from "~/enums/windowComparisonType";
  import organizationMixin from "~/mixins/organizationMixin";
  import ObjectModelTrigger from "~/models/objectModelTriggers/objectModelTrigger";
  export default Vue.extend({
    components: {FormulaWrapper, DatePicker},
    mixins: [organizationMixin],
    props: {
      model: {
        type: Object,
        required: true
      },
      includeBuiltIn:{
        type: Boolean,
        default: false
      },
      fullWidth: {
        type: Boolean,
        default: false
      },
      compress: {
        type: Boolean,
        default: false
      },
      prop_key: String,
      value: Object as () => {
        type: DurationType,
        day: number,
        month: Month,
        amount: number,
        unit: DurationUnit
      },
      additionalClass: {
        type: String,
        default: ''
      },
      options: {
        type: [
          Object as () => TVOptionSet,
          Array as () => TVOptionSet[],
          Array
        ],
        default: () => {
          return [
            {
              options: [],
              endpoint: undefined,
              valueKey: 'value',
              labelKey: 'label',
            }
          ];
        },
        required: true
      },
      unitOptions: {
        type: Array as () => IDefaultVOption[],
        default: () => DurationAndTimeHelper.durationUnitOptions,
      },
    },
    data: function () {
      return {
        DurationType: DurationType,
        DurationAndTimeHelper: DurationAndTimeHelper,
        statusActivationEventVOptions,
        statusActivationDateComparisonVOptions,
        DatetimeMode
      };
    },
    computed: {
      displayName() {
        return this.model.getDisplayName(this.prop_key);
      },
      typeOptions(){
        return this.options.map(x=>{return {label: x.label, value: x.value}})
      },
      windowComparisonOptions(){
        return EnumHelper.toArray(windowComparisonType).map(x => {return {label: windowComparisonTypeName[x], value: x}})
      },
      labelClasses() {
        return {
          required: this.required
        };
      },
      numDays() {
        if (this.value.month === Month.february) {
          return DurationAndTimeHelper.dayOptions.slice(0, 29)
        } else if ([Month.april, Month.june, Month.september, Month.november].includes(this.value.month)) {
          return DurationAndTimeHelper.dayOptions.slice(0, 30)
        } else {
          return DurationAndTimeHelper.dayOptions
        }
      },
      fieldsHandler(){
        if(this.includeBuiltIn){
          return new HandlerOfHandlers([
              {
                displayName: 'About Record',
                handler: new ObjectModelRecordMetadataDynamicFieldsHandler(),
              },
              {
                displayName: 'Object Model Properties',
                handler: new ObjectModelPropertyDynamicFieldsHandler({
                  mode: 'flat',
                  objectModel:this.model.parent
                })
              }
            ]
          )
        }else{
          return new ObjectModelPropertyDynamicFieldsHandler({
            mode: 'flat',
            objectModel: this.model.parent
          })
        }
      },
      dynamicOptions(): IDynamicFieldsOptions[] {
        return [
          {
            is: 'dynamic-fields-tree',
            props: {
              objectModel: this.model.parent,
              parent: this.model[this.prop_key],
              fieldsHandler: this.fieldsHandler,
            },
          },
        ];
      },
      propertyValue: {
        get() {
          return this.value.property_client_id || this.value.builtin_property_type;
        },
        set(option: any) {
          switch(option.type) {
            case DynamicReferenceType.ObjectModelProperty:
              this.value.property_client_id = option.value;
              this.value.builtin_property_type = null;
              break;
            case DynamicReferenceType.ObjectModelRecordMetadata:
              this.value.property_client_id = null;
              this.value.builtin_property_type = option.value;
              break;
          }
        }
      },
    },
    watch: {
      'value.month': function (newVal, oldVal) {
        if (newVal === Month.february && this.value.day > 29) {
          this.value.day = 29
        } else if ([Month.april, Month.june, Month.september, Month.november].includes(newVal) && this.value.day > 30) {
          this.value.day = 30
        }
      },
      propertyValue: function (newPV, oldPV) {
        if (!(this.model instanceof ObjectModelTrigger)) {
          return
        }
        if (![DurationUnit.minute, DurationUnit.hour].includes(this.value.unit)) {
          return
        }
        if (newPV !== 1 && this.model.parent.object_model_properties[newPV].datetime_mode === 0) {
          this.value.unit = 2
        }
      }
    },
    methods:{
      sectionConfigFor(type: DurationType){
        return this.options.find(x => x.value === type)?.sectionConfig
      },
      propertyForSection(type: DurationType, property: string){
        if (!this.sectionConfigFor(type)){
          return null
        }else{
          return this.sectionConfigFor(type)[property]
        }
      },
    },
  });
</script>
